
export const dur = { xs: 0.16, sm: 0.22, md: 0.28, lg: 0.40 } as const;

export const easing = {
  emphasized: [0.2, 0, 0, 1] as const,
  standard: [0.2, 0, 0, 1] as const,
  accelerate: [0.3, 0, 0.8, 0.15] as const,
};

/**
 * Row disclosure — expanding a queue row, a leaderboard row, a variator.
 *
 * Deliberately its own token and much slower than `dur.sm`, which is what this
 * used to run at. 220ms is right for a chip or a colour change; on a panel that
 * unfolds a whole grid of variators it is under the threshold where the eye
 * reads growth at all, so the panel did not appear to open — it appeared to
 * already be open, one frame later. The content fade is shorter than the height
 * and starts once the panel has committed to a direction, so you see the row
 * grow before you are asked to read anything inside it.
 */
export const disclosure = {
  size: 0.42,
  fade: 0.30,
  fadeDelay: 0.09,
  /** Closing is faster than opening — dismissal should not be savoured. */
  close: 0.16,
} as const;

/**
 * ── The first-load sequence ────────────────────────────────────────────────
 *
 * One clock for the three things that arrive on a cold load, so they read as a
 * single orchestrated entrance instead of three independent timers that happen
 * to overlap:
 *
 *      0.00s   the background is simply there — page ground, watermark,
 *              scanlines. Nothing to animate; it is the stage.
 *      0.08s   the top chrome arrives. The disclaimer bar and the header band
 *              share these exact values so that, although they are separate
 *              elements (the header is `position: sticky` and cannot be wrapped
 *              in anything without losing its containing block), they travel in
 *              lockstep and read as one object settling into place.
 *      0.28s   the header's readouts stagger in behind the band — see
 *              header.tsx, where they also wait on their data.
 *      0.30s   the page content follows.
 *
 * `contentDelay` applies to the FIRST mount of the session only. The content's
 * entrance lives in a route template, which remounts on every navigation, and
 * holding a third of a second before every page swap would be a tax on
 * ordinary use to pay for a moment that happens once.
 */
export const appEntrance = {
  chromeDelay: 0.08,
  chromeDur: 0.62,
  chromeShift: 14,
  readoutsDelay: 0.28,
  contentDelay: 0.30,
} as const;

export const spring = {
  soft: { type: "spring", stiffness: 260, damping: 24 } as const,
  crisp: { type: "spring", stiffness: 380, damping: 32, mass: 0.9 } as const,
};

/* ── Entrance ──────────────────────────────────────────────────────────────
 *
 * The scale above is for REACTION — a control answering a click, a row
 * settling after a change. It is fast because it is a reply, and it is small
 * because the eye is already on the thing that moved.
 *
 * Arrival is a different job. Nobody is looking at anything yet; the motion
 * has to say "this is a page, and here is the order to read it in". At 0.22s
 * and 8px that reads as a repaint rather than an arrival, which is why the
 * site felt snappy and lifeless at once: the animations were all present and
 * all imperceptible.
 *
 * So this is deliberately slower and travels further, and it is expressed as
 * whole choreographies rather than loose numbers, so a page reads as one
 * gesture instead of a dozen unrelated ones.
 *
 * Transform and opacity only. Anything here can run on the compositor.
 */

export const entranceDur = {
  /** A single small item joining something already on screen. */
  item: 0.45,
  /** A card or section arriving. */
  section: 0.55,
  /** The page itself. */
  page: 0.70,
} as const;

export const entranceEase = {
  /** Decelerate hard. The default for anything arriving. */
  enter: [0.16, 1, 0.3, 1] as const,
  /** Accelerate away — asymmetry is what stops an exit feeling sluggish. */
  exit: [0.55, 0, 1, 0.45] as const,
  /** A touch of overshoot, for things that should feel like they landed. */
  overshoot: [0.34, 1.56, 0.64, 1] as const,
} as const;

/** Travel distances. Small enough not to shove layout, large enough to see. */
export const entranceShift = {
  sm: 12,
  md: 20,
  lg: 28,
} as const;

/** Gaps between siblings in a cascade. */
export const stagger = {
  /** Dense lists — a leaderboard row after row. */
  tight: 0.045,
  /** Cards down a column. */
  normal: 0.09,
  /** Three podium places, where each one should land on its own. */
  loose: 0.13,
} as const;

/** A section rising into place. */
export const rise = (distance: number = entranceShift.md, duration: number = entranceDur.section) => ({
  hidden: { opacity: 0, y: distance },
  visible: { opacity: 1, y: 0, transition: { duration, ease: entranceEase.enter } },
  exit: { opacity: 0, y: distance * 0.5, transition: { duration: dur.sm, ease: entranceEase.exit } },
});

/**
 * The bubble pop, for the podium.
 *
 * Scale carries this rather than travel: a place on a podium should feel like
 * it was PLACED, not like it slid in from somewhere. The overshoot curve
 * settles just past 1 and comes back, which is the whole effect — starting at
 * 0.9 keeps the text from resampling badly on the way up.
 */
export const pop = (delay = 0) => ({
  hidden: { opacity: 0, scale: 0.9, y: entranceShift.sm },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: entranceDur.section, ease: entranceEase.overshoot, delay },
  },
});

/**
 * The downward flip, for list rows.
 *
 * rotateX from a top-edge origin, so the row hinges down into place like a
 * card being dealt onto a stack. The parent needs a perspective for this to
 * read as depth rather than as a squash — see `PERSPECTIVE` below.
 *
 * Kept to -14deg on purpose: past about 20 the text goes through a visibly
 * blurry intermediate, because the rasteriser is sampling a plane that is
 * genuinely at an angle.
 */
export const flipDown = (delay = 0) => ({
  hidden: { opacity: 0, rotateX: -14, y: -6 },
  visible: {
    opacity: 1,
    rotateX: 0,
    y: 0,
    transition: { duration: entranceDur.item, ease: entranceEase.enter, delay },
  },
});

/** Put this on the container of anything using `flipDown`. */
export const PERSPECTIVE = { perspective: 900, transformStyle: 'preserve-3d' } as const;
